import { ReactNode, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  BreadcrumbHeading,
  Chip,
  Info,
  Popover,
  RadioSelect,
  Spinner,
  Typography,
} from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import { useTimetable } from 'src/hooks/useTimetable';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { TimetableDay } from 'src/components/TimetableDay';
import styles from './Timetable.module.scss';

export enum TimetableView {
  MENUS_AND_PRICING = 'MENUS_AND_PRICING',
  MENUS_ONLY = 'MENUS_ONLY',
  PRICING_ONLY = 'PRICING_ONLY',
}

const TIMETABLE_VIEW_OPTIONS = [
  { id: TimetableView.MENUS_AND_PRICING, label: 'Menus & Pricing' },
  { id: TimetableView.MENUS_ONLY, label: 'Menus only' },
  { id: TimetableView.PRICING_ONLY, label: 'Pricing only' },
];

interface TimetableTooltipProps {
  label: string;
  children: ReactNode;
}

const TimetableTooltip = ({ label, children }: TimetableTooltipProps) => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <button type="button" aria-label={label}>
        <Info />
      </button>
    </Popover.Trigger>
    <Popover.Content side="bottom" align="start" sideOffset={4}>
      <div className={styles['tooltip']}>
        <Typography colour="secondary" size="small">
          {children}
        </Typography>
      </div>
    </Popover.Content>
  </Popover.Root>
);

export const Timetable = () => {
  const { timetableId } = useParams<{ timetableId?: string }>();
  const [timetableView, setTimetableView] = useState(TimetableView.MENUS_AND_PRICING);

  const { data, byDay, isLoading } = useTimetable(timetableId);
  const timetable = data?.data;
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();

  if (isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  if (!timetableId || !timetable) return <Navigate to={Routes.TIMETABLES} replace />;

  return (
    <div className={styles['page-container']}>
      <BreadcrumbHeading
        breadcrumbs={[
          { path: Routes.TIMETABLES, label: 'Timetables' },
          { path: `${Routes.TIMETABLES}/${timetable.timetable_id}`, label: timetable.name },
        ]}
        subtitle={`Last updated at ${formatDate(new Date(timetable.update_date))}`}
      />
      {rangesEnabled && (
        <div className={styles['ranges']}>
          <Typography size="small" className={styles['label']}>
            {timetable.ranges.length === 0 ? 'Range(s) - No Ranges' : 'Range(s) -'}
          </Typography>
          {timetable.ranges.map((range) => (
            <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
          ))}
        </div>
      )}
      <div className={styles['toolbar']}>
        <RadioSelect
          label="Timetable View"
          name="timetable-view"
          options={[
            {
              options: TIMETABLE_VIEW_OPTIONS,
              selected: timetableView,
              onChange: (id) => setTimetableView(id as TimetableView),
            },
          ]}
        />
      </div>
      <div className={styles['list']}>
        <div className={styles['day-range']}>
          <div className={styles['day-range-start']}>
            <Typography size="small" className={styles['label']}>
              Day Start (00:00)
            </Typography>
            <TimetableTooltip label="Day start information">
              The timetable must cover a 24-hour period, which is determined by your brand&apos;s trading hours. Venue
              open and close are determined by your venue&apos;s opening hours in the Venue Details Page
            </TimetableTooltip>
          </div>
          <Typography size="small" className={`${styles['label']} ${styles['day-range-end']}`}>
            Day End (23:59)
          </Typography>
        </div>
        <div className={styles['days']}>
          {byDay.map((day) => (
            <TimetableDay key={day.day} day={day} view={timetableView} onAddSchedule={() => {}} isActionable />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Timetable;
